import { Category, ScorecardData, Metric } from '../types/scorecard';

// Helper function to create metrics with team ownership, status and hierarchy
const createMetric = (
    id: string,
    name: string,
    description: string,
    owner: string = '',
    isActive: boolean = true,
    subMetrics: Metric[] = []
): Metric => ({
    id,
    name,
    description,
    value: '',
    target: '',
    owner,
    isActive,
    lastUpdated: new Date(),
    subMetrics
});

export const enhancedCategories: Category[] = [
    {
        id: 'client',
        name: 'CLIENT',
        description: 'Revenue & Market Access',
        goal: 'Primary goal: US Health System Adoption, Clinical Volume, and Revenue Expansion. ',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / US Entry',
                description: 'CLIENT - Market Expansion & Usage ',
                metrics: [
                    createMetric(
                        'l1-us-health-adoption',
                        '1. US Health System Adoption',
                        'Number of live health systems using AI-ECG in clinical workflow. ',
                        'CMO',
                        true,
                        [
                            createMetric(
                                'cro-partnership-program',
                                'CRO Partnership Program',
                                'Active partnerships generating pre-regulatory revenue',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'partnership-acquisition',
                                        'Partnership Acquisition',
                                        'Weekly pipeline and conversion tracking',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('daily-cro-outreach', 'Daily CRO Outreach', '# new CRO contacts made', 'CMO', true),
                                            createMetric('proposal-pipeline', 'Proposal Pipeline', 'Proposals pending', 'CMO', true),
                                            createMetric('demo-requests', 'Demo Requests', 'Inbound requests', 'CMO', true),
                                            createMetric('decision-timeframe', 'Decision Timeframe', 'Negotiation days', 'CMO', true),
                                            createMetric('global-cro-expansion', 'Global CRO Expansion', 'International development', 'CMO', false),
                                            createMetric('cro-contract-optimization', 'CRO Contract Optimization', 'Revenue optimization', 'CMO', false),
                                            createMetric('competitive-cro-analysis', 'Competitive CRO Analysis', 'Win/loss analysis', 'CMO', false),
                                            createMetric('cro-referral-network', 'CRO Referral Network', 'Referral tracking', 'CMO', false)
                                        ]
                                    )
                                ]
                            ),
                            createMetric(
                                'event-marketing-outreach',
                                'Event Marketing & Outreach',
                                'Conference outcomes and brand visibility',
                                'CEO',
                                true,
                                [
                                    createMetric(
                                        'acc-2026',
                                        'ACC 2026',
                                        'Engagement metrics for ACC 2026',
                                        'CEO',
                                        true,
                                        [
                                            createMetric('acc-2026-contacts', 'Contacts Spoke To', '18/50', 'CEO', true),
                                            createMetric('acc-2026-meetings', 'Further Meetings', '6/18', 'CEO', true),
                                            createMetric('acc-2026-personal-followers', 'New Personal Followers', '60', 'CEO', true),
                                            createMetric('acc-2026-linkedin-followers', 'New LinkedIn Followers', '22', 'CEO', true)
                                        ]
                                    )
                                ]
                            ),
                            createMetric(
                                'post-clearance-readiness',
                                'Post-Clearance Market Readiness',
                                'Preparation for scaled commercial deployment',
                                'CMO',
                                false,
                                [
                                    createMetric(
                                        'ehr-integration-pipeline',
                                        'EHR Integration Pipeline',
                                        'Health system integration partnerships',
                                        'CTO',
                                        false,
                                        [
                                            createMetric('epic-integration-progress', 'Epic Progress', '% completion', 'CTO', false),
                                            createMetric('api-certification', 'API Certification', 'Vendor certifications', 'CTO', false),
                                            createMetric('pilot-health-systems', 'Pilot Health Systems', '# systems in pilot', 'CMO', false),
                                            createMetric('multi-ehr-compatibility-testing', 'Multi-EHR Compatibility', 'Validation', 'CTO', false),
                                            createMetric('enterprise-deployment-automation', 'Deployment Automation', 'Scaled networks', 'CTO', false),
                                            createMetric('white-label-partnership-development', 'White-label Dev', 'EHR white-label', 'CMO', false),
                                            createMetric('interoperability-standard-compliance', 'Interoperability', 'FHIR/Standards', 'CTO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-clinical-usage-volume',
                        '2. Clinical Usage Volume',
                        'Total ECG analyses processed in live environments. ',
                        'CMO',
                        true,
                        [
                            createMetric(
                                'nhs-validation-program',
                                'NHS Validation Program',
                                'Evidence generation through NHS pilot',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'pilot-execution',
                                        'Pilot Execution',
                                        'NHS pilot operational performance',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('daily-ecg-volume', 'Daily ECG Volume', '# processed', 'CMO', true),
                                            createMetric('clinician-adoption', 'Clinician Adoption', '# active clinicians', 'CMO', true),
                                            createMetric('data-quality-score', 'Data Quality Score', '% passing QC', 'CTO', true),
                                            createMetric('technical-issues', 'Technical Issues', '# issues reported', 'CTO', true),
                                            createMetric('nhs-trust-expansion', 'NHS Trust Expansion', 'Post-pilot growth', 'CMO', false),
                                            createMetric('clinician-workflow-efficiency', 'Workflow Efficiency', 'Time saved', 'CMO', false),
                                            createMetric('patient-pathway-optimization', 'Patient Pathway', 'AI integration', 'CMO', false),
                                            createMetric('interoperability-performance', 'Interoperability Perf', 'NHS systems', 'CTO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'partnership-delivery',
                                        'Partnership Delivery',
                                        'Active partnership performance and satisfaction',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('trial-ecgs-processed', 'Trial ECGs Processed', '# ECGs analyzed', 'CMO', true),
                                            createMetric('turnaround-time', 'Turnaround Time', 'Analysis speed', 'CTO', true),
                                            createMetric('enrichment-performance', 'Enrichment Perf', 'Event rate vs baseline', 'CTO', true),
                                            createMetric('sponsor-satisfaction', 'Sponsor Satisfaction', 'Weekly partner score', 'CMO', true),
                                            createMetric('multi-study-coordination', 'Multi-Study Coord', 'Simultaneous mgmt', 'CMO', false),
                                            createMetric('automated-reporting-delivery', 'Automated Reporting', 'Report delivery', 'CTO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-revenue-expansion',
                        '3. Revenue Expansion & Retention',
                        '% of recurring revenue retained and upsold. ',
                        'CMO',
                        true,
                        [
                            createMetric(
                                'revenue-scaling-l2',
                                'Revenue Generation & Expansion',
                                'Scaling pre-regulatory revenue paths (Old L2 Shifted)',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'revenue-generation',
                                        'Revenue Generation',
                                        'CRO partnership revenue',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('monthly-recurring-revenue', 'MRR', 'Recurring revenue', 'CMO', false),
                                            createMetric('per-ecg-pricing', 'Per-ECG Pricing', 'Revenue per analysis', 'CMO', true),
                                            createMetric('payment-collections', 'Payment Collections', 'Invoice timeframes', 'CSO', true),
                                            createMetric('revenue-pipeline', 'Revenue Pipeline', 'Signed deal projections', 'CMO', true),
                                            createMetric('dynamic-pricing-optimization', 'Dynamic Pricing', 'AI-driven pricing', 'CMO', false),
                                            createMetric('customer-lifetime-value-tracking', 'CLV Tracking', 'CLV per CRO', 'CMO', false),
                                            createMetric('revenue-per-therapeutic-area', 'Therapeutic Area Rev', 'Segmentation', 'CMO', false),
                                            createMetric('contract-renewal-revenue-growth', 'Renewal Growth', '% increase', 'CMO', false),
                                            createMetric('upsell-cross-sell-conversion', 'Upsell Conversion', 'Service penetration', 'CMO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-clinician-confidence',
                        '4. Clinician Confidence & Advocacy',
                        'Diagnostic confidence and evidence-based stickiness. ',
                        'CMO',
                        true,
                        [
                            createMetric(
                                'confidence-evidence-l2',
                                'Confidence & Outcomes',
                                'Clinical validity and stickiness metrics (Old L2 Shifted)',
                                'CMO',
                                true,
                                [
                                    createMetric(
                                        'clinical-outcomes',
                                        'Clinical Outcomes',
                                        'Patient outcome tracking',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('flag-accuracy', 'Flag Accuracy', '% AI flags confirmed', 'CTO', true),
                                            createMetric('clinical-actions', 'Clinical Actions', '% interventions', 'CMO', true),
                                            createMetric('outcome-follow-up', 'Outcome Follow-up', 'Completion rates', 'CMO', true),
                                            createMetric('adverse-events', 'Adverse Events', 'Safety tracking', 'QARA', true),
                                            createMetric('population-health-impact', 'Population Health', 'Event reduction', 'CMO', false),
                                            createMetric('health-economic-outcomes', 'Health Economics', 'Cost per QALY', 'CSO', false),
                                            createMetric('long-term-outcome-tracking', 'Long-term Tracking', '5-year studies', 'CSO', false),
                                            createMetric('mortality-morbidity-impact', 'Morbidity Impact', 'Population level', 'CSO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'clinical-engagement',
                        '4. Clinical Engagement Score',
                        'Overall clinician satisfaction and adoption metrics',
                        'CMO',
                        true,
                        []
                    )
                ]
            }
        ]
    },
    {
        id: 'control',
        name: 'CONTROL',
        description: 'Regulatory, Compliance & Certification',
        goal: 'Dual pathway: FDA/EUMDR submission and QMS/IEC 62304 foundation.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Compliance',
                description: 'CONTROL - Regulatory & Maint Velocity ',
                metrics: [
                    createMetric(
                        'l1-regulatory-velocity',
                        'Regulatory Velocity',
                        'Speed and quality of regulatory submissions. ',
                        'CSO',
                        true,
                        [
                            createMetric(
                                'regulatory-pathway-execution',
                                'Regulatory Execution',
                                'Dual FDA/EUMDR milestone achievement',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'fda-510k-submission',
                                        'FDA 510(k) Submission',
                                        'LEF 510(k) progress',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('predicate-analysis', 'Predicate Analysis', 'Anumana comparison', 'CSO', true),
                                            createMetric('clinical-data-package', 'Clinical Data', '% evidence complete', 'CSO', true),
                                            createMetric('fda-pre-sub-meeting', 'FDA Pre-Sub', 'Meeting prep', 'CSO', true),
                                            createMetric('estar-submission-prep', 'eSTAR Prep', '% package complete', 'CSO', true),
                                            createMetric('regulatory-consultant-progress', 'Consultant Progress', 'JREG milestones', 'CSO', true),
                                            createMetric('fda-annual-report-compliance', 'FDA Annual Report', 'MDR compliance', 'QARA', false),
                                            createMetric('fda-inspection-readiness', 'FDA Readiness', 'QS inspection prep', 'QARA', false),
                                            createMetric('breakthrough-device-designation-maintenance', 'Breakthrough Maint', 'Benefits', 'CSO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'eumdr-ce-marking',
                                        'EUMDR CE Marking',
                                        'Class IIa submission progress',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('notified-body-engagement', 'NB Engagement', 'Scarlet resolution', 'CSO', true),
                                            createMetric('technical-file-completion', 'Technical File', '% complete', 'QARA', true),
                                            createMetric('clinical-evaluation-report', 'CER', '9 models CER', 'CSO', true),
                                            createMetric('usability-engineering-file', 'Usability File', 'IEC 62366-1', 'CTO', true),
                                            createMetric('pmcf-plan-development', 'PMCF Plan', 'Vigilance planning', 'CSO', true),
                                            createMetric('eudamed-compliance-tracking', 'EUDAMED Compliance', 'EU database', 'QARA', false),
                                            createMetric('mdr-periodic-safety-update-reports', 'PSUR Compliance', 'PSUR generation', 'QARA', false)
                                        ]
                                    ),
                                    createMetric(
                                        'de-novo-preparation',
                                        'De Novo Preparation',
                                        'Multi-disease novel pathway prep',
                                        'CSO',
                                        false,
                                        [
                                            createMetric('novel-predicate-research', 'Predicate Research', 'Landscape', 'CSO', false),
                                            createMetric('multi-model-validation', 'Multi-Model Valid', '# models validated', 'CTO', false),
                                            createMetric('clinical-evidence-expansion', 'Evidence Expansion', 'Novel pathway', 'CSO', false),
                                            createMetric('fda-breakthrough-designation', 'Breakthrough Pursuit', 'Designation status', 'CSO', false),
                                            createMetric('ai-ml-regulatory-framework-development', 'AI/ML Framework', 'Regulatory science', 'CSO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-regulatory-maint-spend',
                        'Regulatory Maint. Spend',
                        'Compliance as a recurring tax; post-market and QMS audits. ',
                        'QARA',
                        true,
                        [
                            createMetric(
                                'qms-foundation',
                                'QMS Foundation',
                                'ISO 13485 implementation',
                                'QARA',
                                true,
                                [
                                    createMetric(
                                        'iso-13485-implementation',
                                        'ISO 13485 Build',
                                        'Audit readiness',
                                        'QARA',
                                        true,
                                        [
                                            createMetric('sop-development', 'SOP Development', '# written vs required', 'QARA', true),
                                            createMetric('document-control', 'Document Control', 'System implementation', 'QARA', true),
                                            createMetric('management-review-cadence', 'Management Review', 'Execution', 'QARA', true),
                                            createMetric('internal-audit-schedule', 'Internal Audit', 'Planning/Execution', 'QARA', true),
                                            createMetric('capa-system', 'CAPA System', 'Setup and tracking', 'QARA', true),
                                            createMetric('qms-maturity-assessment', 'QMS Maturity', 'Benchmarking', 'QARA', false),
                                            createMetric('quality-cost-optimization', 'Quality Cost', 'Reduction', 'QARA', false),
                                            createMetric('multi-site-qms-coordination', 'Multi-Site Coord', 'Global QMS', 'QARA', false)
                                        ]
                                    ),
                                    createMetric(
                                        'risk-management',
                                        'Risk Management',
                                        'ISO 14971 files',
                                        'QARA',
                                        true,
                                        [
                                            createMetric('hazard-analysis', 'Hazard Analysis', 'Identification per model', 'QARA', true),
                                            createMetric('risk-control-measures', 'Risk Controls', 'Mitigation tracking', 'QARA', true),
                                            createMetric('residual-risk-evaluation', 'Residual Risk', 'Post-mitigation', 'QARA', true),
                                            createMetric('risk-benefit-analysis', 'Risk-Benefit', 'Benefit vs risk', 'QARA', true),
                                            createMetric('fmea-completion', 'FMEA Completion', 'Analysis coverage', 'QARA', true),
                                            createMetric('dynamic-risk-assessment', 'Dynamic Risk', 'Real-time monitoring', 'QARA', false),
                                            createMetric('enterprise-risk-integration', 'Enterprise Risk', 'Broad integration', 'QARA', false)
                                        ]
                                    )
                                ]
                            ),
                            createMetric(
                                'software-lifecycle-compliance',
                                'Software Lifecycle Compliance',
                                'IEC 62304 implementation',
                                'CTO',
                                true,
                                [
                                    createMetric(
                                        'software-development-planning',
                                        'Development Planning',
                                        'IEC 62304 process',
                                        'CTO',
                                        true,
                                        [
                                            createMetric('software-safety-classification', 'Safety Class', 'Class A/B/C', 'CTO', true),
                                            createMetric('software-development-plan', 'Dev Plan', 'Documentation', 'CTO', true),
                                            createMetric('software-requirements-spec', 'SRS', 'Documentation', 'CTO', true),
                                            createMetric('software-design-spec', 'SDS', 'Architecture', 'CTO', true),
                                            createMetric('verification-validation-plan', 'V&V Plan', 'Protocol planning', 'CTO', true),
                                            createMetric('ai-model-version-control-compliance', 'Model Versioning', 'Change control', 'CTO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'cybersecurity-compliance',
                                        'Cybersecurity Compliance',
                                        'IEC 81001-5-1 implementation',
                                        'CTO',
                                        true,
                                        [
                                            createMetric('threat-modeling', 'Threat Modeling', 'Security analysis', 'CTO', true),
                                            createMetric('penetration-testing', 'Penetration Testing', 'External testing', 'CTO', true),
                                            createMetric('vulnerability-management', 'Vulnerability Mgmt', 'Findings by severity', 'CTO', true),
                                            createMetric('sbom-maintenance', 'SBOM Maintenance', 'BOM currency', 'CTO', true),
                                            createMetric('continuous-security-monitoring', 'Security Monitoring', '24/7 monitoring', 'CTO', false),
                                            createMetric('compliance-framework-integration', 'Framework Integration', 'SOC2/HIPAA', 'CTO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    )
                ]
            }
        ]
    },
    {
        id: 'cost',
        name: 'COST',
        description: 'Burn, Runway & Unit Economics',
        goal: 'Managing burn rate and modeling unit economics.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Financials',
                description: 'COST - Acquisition & Operating Margins ',
                metrics: [
                    createMetric(
                        'l1-us-customers',
                        '1. US Customers',
                        'CAC and ROI targets for US Market Entry. ',
                        'CSO',
                        true,
                        [
                            createMetric(
                                'financial-runway-l2',
                                'Financial Runway & Fundraising',
                                'Cash management and runway extension (Old L1 Shifted)',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'burn-rate-tracking',
                                        'Burn Rate Tracking',
                                        'Cash consumption monitoring',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('daily-cash-burn', 'Daily Cash Burn', 'Expense tracking', 'CSO', true),
                                            createMetric('payroll-expenses', 'Payroll Expenses', 'Personnel tracking', 'CSO', true),
                                            createMetric('cloud-infrastructure-costs', 'Cloud Infra Costs', 'AWS/Compute', 'CTO', true),
                                            createMetric('regulatory-consulting-costs', 'Regulatory Costs', 'External NB/Consultant', 'CSO', true),
                                            createMetric('rd-expenses', 'R&D Expenses', 'Development allocation', 'CTO', true),
                                            createMetric('departmental-cost-center-optimization', 'Cost Center Opt', 'Business unit allocation', 'CSO', false),
                                            createMetric('tax-optimization-strategy-execution', 'Tax Optimization', 'Efficiency', 'CSO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'fundraising-readiness',
                                        'Fundraising Readiness',
                                        'Next round preparation',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('investor-meetings-scheduled', 'Investor Meetings', '# meetings per week', 'CSO', true),
                                            createMetric('due-diligence-readiness', 'Due Diligence', '% data room complete', 'CSO', true),
                                            createMetric('term-sheet-negotiations', 'Term Sheets', 'Active discussions', 'CSO', true),
                                            createMetric('investor-follow-up-queue', 'Follow-up Queue', 'Investor action items', 'CSO', true),
                                            createMetric('institutional-investor-relationship-depth', 'Investor Depth', 'Tier 1 scoring', 'CSO', false),
                                            createMetric('roadshow-effectiveness-metrics', 'Roadshow Metrics', 'Conversion tracking', 'CSO', false),
                                            createMetric('secondary-market-activity-monitoring', 'Secondary Market', 'Trading dynamics', 'CSO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-gross-margin',
                        '2. Gross Margin / Analysis',
                        'Target >75% softare margins as volume scales. ',
                        'CSO',
                        true,
                        [
                            createMetric(
                                'unit-economics-l2',
                                'Unit Economics & Profitability',
                                'Modeling post-clearance profitability (Old L1 Shifted)',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'unit-economics-modeling',
                                        'Unit Economics Modeling',
                                        'Post-clearance pricing model',
                                        'CSO',
                                        false,
                                        [
                                            createMetric('cost-per-analysis', 'Cost Per Analysis', 'Unit cost at scale', 'CTO', false),
                                            createMetric('compute-cost-optimization', 'Compute Opt', 'GPU cost per ECG', 'CTO', false),
                                            createMetric('storage-cost-modeling', 'Storage Modeling', 'Data retention cost', 'CTO', false),
                                            createMetric('support-cost-allocation', 'Support Allocation', 'Support cost/analysis', 'CMO', false),
                                            createMetric('regulatory-overhead-amortization', 'Regulatory Overhead', 'Overhead per unit', 'CSO', false),
                                            createMetric('ml-model-efficiency-optimization', 'ML Efficiency', 'Inference efficiency', 'CTO', false),
                                            createMetric('third-party-api-cost-management', 'API Cost Mgmt', 'Service optimization', 'CTO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'gross-margin-optimization',
                                        'Gross Margin Optimization',
                                        'Path to target margin',
                                        'CSO',
                                        false,
                                        [
                                            createMetric('cogs-reduction-initiatives', 'COGS Reduction', 'Direct cost optimization', 'CTO', false),
                                            createMetric('margin-improvement-roadmap', 'Margin Roadmap', 'Pathway to target', 'CSO', false),
                                            createMetric('customer-segment-profitability-analysis', 'Segment Profitability', 'Margin by segment', 'CMO', false),
                                            createMetric('economies-of-scale-realization', 'Economies of Scale', 'Scale benefits', 'CSO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    )
                ]
            }
        ]
    },
    {
        id: 'colleague',
        name: 'COLLEAGUE',
        description: 'Team, IP & Network',
        goal: 'Key hires and scientific moat.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Talent',
                description: 'COLLEAGUE - Performance & Retention ',
                metrics: [
                    createMetric(
                        'time-to-hire',
                        '1. Time to Hire',
                        'Average days from role posting to hire completion',
                        'CSO',
                        true,
                        []
                    ),
                    createMetric(
                        'succession-planning',
                        '2. Percentage of Roles with Proper Succession',
                        '% of critical roles with identified successors',
                        'CSO',
                        true,
                        []
                    ),
                    createMetric(
                        'retention-rates-top-quartile',
                        '3. Retention Rates of Top Quartile',
                        '% of top performers retained over 12 months',
                        'CSO',
                        true,
                        []
                    ),
                    createMetric(
                        'attrition-rates',
                        '4. Attrition Rates (Including Good Attrition)',
                        'Total attrition % and bottom quartile departure %',
                        'CSO',
                        true,
                        []
                    ),
                    createMetric(
                        'training-days',
                        '5. Number of Training Days',
                        'Total training days per employee per quarter',
                        'CSO',
                        true,
                        []
                    ),
                    createMetric(
                        'l1-tech-retention',
                        '6. Technical Talent Retention',
                        'IP Protection and turnover management for senior R&D staff. ',
                        'CSO',
                        true,
                        [
                            createMetric(
                                'talent-retention-l2',
                                'Hiring Execution & IP',
                                'Talent acquisition and intellectual property (Old L1 Shifted)',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'critical-hiring-execution',
                                        'Critical Hiring Execution',
                                        'Role fulfillment for regulatory/technical',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('iec-62304-developer-hiring', 'IEC 62304 Dev', 'Recruitment pipeline', 'CTO', true),
                                            createMetric('candidate-sourcing', 'Candidate Sourcing', '# qualified IEC 62304', 'CTO', true),
                                            createMetric('technical-assessment-results', 'Technical Assessment', '% passing technical', 'CTO', true),
                                            createMetric('time-to-hire', 'Time to Hire', 'Days from post to offer', 'CTO', true),
                                            createMetric('regulatory-expertise-acquisition', 'Regulatory Hiring', 'RA/QA role fulfillment', 'CSO', true),
                                            createMetric('internal-regulatory-hiring', 'Internal RA Hiring', 'In-house team building', 'CSO', true)
                                        ]
                                    ),
                                    createMetric(
                                        'intellectual-property-protection',
                                        'IP Protection',
                                        'Patent portfolio and scientific strategy',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('patent-application-filing', 'Patent Filing', 'Imperial College progress', 'CSO', true),
                                            createMetric('freedom-to-operate-analysis', 'FTO Analysis', 'Landscape review', 'CSO', true),
                                            createMetric('patent-prosecution-management', 'Patent Prosecution', 'Office response', 'CSO', true),
                                            createMetric('manuscript-development', 'Manuscript Dev', '# papers in submission', 'CSO', true),
                                            createMetric('peer-review-timeline', 'Peer Review Timeline', 'Revision cycles', 'CSO', true),
                                            createMetric('citation-impact-tracking', 'Citation Tracking', 'Impact metrics', 'CSO', true)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-workflow-integration',
                        '3. Clinical Workflow Integration',
                        'Onboarding speed and IT integration quality. ',
                        'CTO',
                        true,
                        [
                            createMetric(
                                'integration-and-network-l2',
                                'Workflow & Strategic Network',
                                'Clinical integration and network development (Old L1 Shifted)',
                                'CTO',
                                true,
                                [
                                    createMetric(
                                        'strategic-network-development',
                                        'Strategic Network',
                                        'KOL and Industry relations',
                                        'CMO',
                                        true,
                                        [
                                            createMetric('kol-identification-outreach', 'KOL Outreach', '# new relations', 'CMO', true),
                                            createMetric('advisory-board-engagement', 'Advisory Board', 'Meeting frequency', 'CMO', true),
                                            createMetric('kol-referral-generation', 'KOL Referrals', '# warm intros', 'CMO', true),
                                            createMetric('cro-relationship-depth', 'CRO Depth', 'Quality scoring', 'CMO', true),
                                            createMetric('pharma-sponsor-introductions', 'Pharma Intros', 'Sponsor engagement', 'CMO', true),
                                            createMetric('regulatory-support-network', 'Regulatory Network', 'Agency relations', 'CSO', true),
                                            createMetric('regulatory-letter-support', 'Letter Support', 'Submission letters', 'CSO', true)
                                        ]
                                    ),
                                    createMetric(
                                        'product-deployment-readiness',
                                        'Product Readiness',
                                        'Deployment readiness at scale',
                                        'CTO',
                                        true,
                                        [
                                            createMetric('clinical-workflow-integration', 'Clinical Workflow', 'Workflow compatibility', 'CTO', true),
                                            createMetric('ehr-integration-testing', 'EHR Testing', 'FHIR compliance', 'CTO', true),
                                            createMetric('clinical-user-interface', 'Clinical UI', 'Interface usability', 'CTO', true),
                                            createMetric('report-generation-quality', 'Report Quality', 'Accuracy/Formatting', 'CTO', true),
                                            createMetric('workflow-efficiency-testing', 'Efficiency Testing', 'Workflow impact', 'CTO', true)
                                        ]
                                    )
                                ]
                            )
                        ]
                    )
                ]
            }
        ]
    },
    {
        id: 'change',
        name: 'CHANGE',
        description: 'Product, Models & Competitive Position',
        goal: 'Innovation velocity and competitive market fit.',
        levels: [
            {
                level: 1,
                name: 'Strategic Theme / Velocity',
                description: 'CHANGE - Innovation & Win Rate ',
                metrics: [
                    createMetric(
                        'l1-product-velocity',
                        '1. Product/Model Velocity',
                        'Speed and quality of new AI model releases. ',
                        'CTO',
                        true,
                        [
                            createMetric(
                                'model-validation-l2',
                                'AI Model Validation',
                                'R&D velocity for model bundles (Old L1 Shifted)',
                                'CTO',
                                true,
                                [
                                    createMetric(
                                        'ai-model-validation-program',
                                        'AI Model Validation',
                                        'Validation of 9 models',
                                        'CTO',
                                        true,
                                        [
                                            createMetric('model-performance-optimization', 'Performance Opt', 'Accuracy/Calibration', 'CTO', true),
                                            createMetric('model-training-cycles', 'Training Cycles', 'Experiments/week', 'CTO', true),
                                            createMetric('auc-improvement-tracking', 'AUC Tracking', 'Progression per model', 'CTO', true),
                                            createMetric('validation-dataset-coverage', 'Dataset Coverage', '% demographics', 'CTO', true),
                                            createMetric('model-interpretability-testing', 'Interpretability', 'Explainability features', 'CTO', true),
                                            createMetric('automated-model-retraining-pipeline', 'Retraining Pipeline', 'Automation', 'CTO', false),
                                            createMetric('model-drift-detection-alerting', 'Drift Detection', 'Performance degradation', 'CTO', false),
                                            createMetric('federated-learning-performance-optimization', 'Federated Learning', 'Distributed efficiency', 'CTO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'regulatory-validation-evidence',
                                        'Validation Evidence',
                                        'Clinical evidence generation',
                                        'CTO',
                                        true,
                                        [
                                            createMetric('clinical-validation-studies', 'Validation Studies', 'Independent study execution', 'CTO', true),
                                            createMetric('real-world-performance', 'Real-World Perf', 'NHS/CRO performance', 'CTO', true),
                                            createMetric('bias-fairness-assessment', 'Bias Assessment', 'Algorithmic fairness', 'CTO', true),
                                            createMetric('longitudinal-model-performance-tracking', 'Longitudinal Tracking', 'Stability post-launch', 'CTO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    ),
                    createMetric(
                        'l1-competitive-win-rate',
                        '3. Competitive Win Rate',
                        'Market Fit: Wins in head-to-head pilots. ',
                        'CSO',
                        true,
                        [
                            createMetric(
                                'intelligence-positioning-l2',
                                'Competitive Intelligence',
                                'Landscape monitoring and positioning (Old L1 Shifted)',
                                'CSO',
                                true,
                                [
                                    createMetric(
                                        'competitive-intelligence-positioning',
                                        'Competitive Positioning',
                                        'Advantage maintenance',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('competitive-landscape-monitoring', 'Landscape Monitoring', 'Competitor analysis', 'CSO', true),
                                            createMetric('competitor-regulatory-filings', 'Competitor Filings', 'FDA/EMA tracking', 'CSO', true),
                                            createMetric('competitive-performance-benchmarking', 'Benchmarking', 'H2H comparison', 'CSO', true),
                                            createMetric('patent-landscape-monitoring', 'Patent Monitoring', 'IP activity tracking', 'CSO', true),
                                            createMetric('competitive-partnership-intelligence', 'Partnership Intel', 'CRO/Pharma deals', 'CMO', true),
                                            createMetric('competitive-win-rate-analysis', 'Win Rate Analysis', 'Loss reasons vs features', 'CMO', false),
                                            createMetric('social-media-competitive-sentiment', 'Competitive Sentiment', 'Brand vs competitors', 'CMO', false)
                                        ]
                                    ),
                                    createMetric(
                                        'differentiation-strategy-execution',
                                        'Differentiation Strategy',
                                        'Unique value proposition',
                                        'CSO',
                                        true,
                                        [
                                            createMetric('clinical-evidence-superiority', 'Evidence Superiority', 'Evidence vs competitors', 'CSO', true),
                                            createMetric('technology-advantage-communication', 'Tech Differentiation', 'Messaging', 'CMO', true),
                                            createMetric('market-positioning-effectiveness', 'Positioning Effectiveness', 'Brand recognition', 'CMO', true),
                                            createMetric('thought-leadership-development', 'Thought Leadership', 'Industry recognition', 'CSO', true),
                                            createMetric('regulatory-pathway-leadership', 'Regulatory Leadership', 'Defining pathways', 'CSO', false)
                                        ]
                                    )
                                ]
                            )
                        ]
                    )
                ]
            }
        ]
    }
];

export const enhancedScorecardData: ScorecardData = {
    title: "CARDIOVOLT.AI — BALANCED SCORECARD",
    subtitle: "Pre-Revenue SaMD | February 2026",
    currentVersion: enhancedCategories,
    versions: [],
    lastModified: new Date()
};